import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Table, Badge, Alert, Spinner, Modal, Container } from "react-bootstrap";
import { Plus, Trash2, Power, PowerOff, Users, BarChart2 } from "lucide-react";
import { Funcionario, FuncionarioFormData } from "../../types";
import { useFuncionarios } from "../../hooks/useFuncionarios";
import { useAuth } from "../../hooks/useAuth";
import FuncionarioForm from "./FuncionarioForm";
import ConfirmModal from "../Common/ConfirmModal";
import MainHeader from "../Common/MainHeader/MainHeader";
import { toast } from "react-toastify";
import "./FuncionariosList.css";
import { Button } from "../../app/components";
import { DataList, DataListColumn } from "../Common/DataList";

const FuncionariosList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    funcionarios,
    loading,
    error,
    addFuncionario,
    toggleFuncionarioStatus,
    removeFuncionario,
  } = useFuncionarios(user?.uid || "");

  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [funcionarioToDelete, setFuncionarioToDelete] =
    useState<Funcionario | null>(null);
  const [funcionarioToEdit, setFuncionarioToEdit] =
    useState<Funcionario | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newFuncionario, setNewFuncionario] = useState<Funcionario | null>(
    null,
  );

  const handleAddFuncionario = async (funcionarioData: FuncionarioFormData) => {
    const result = await addFuncionario(funcionarioData);
    if (result.success && result.data) {
      setShowForm(false);
      setNewFuncionario(result.data);
      setShowSuccessModal(true);
    }
  };

  const handleDeleteFuncionario = (funcionario: Funcionario) => {
    setFuncionarioToDelete(funcionario);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (funcionarioToDelete) {
      await removeFuncionario(funcionarioToDelete.id!);
      setShowDeleteModal(false);
      setFuncionarioToDelete(null);
    }
  };

  const handleToggleStatus = async (funcionario: Funcionario) => {
    await toggleFuncionarioStatus(funcionario.id!, !funcionario.ativo);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return "-";
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  const columns: DataListColumn<Funcionario>[] = [
    {
      label: "Funcionário",
      name: "nome",
      size: 3,
      render: (funcionario) => (
        <div className="d-flex align-items-center">
          <div className="funcionario-avatar me-3">
            {funcionario.nome.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="fw-medium">{funcionario.nome}</div>
          </div>
        </div>
      ),
    },
    {
      label: "Código de Acesso",
      name: "codigo",
      size: 3,
      render: (funcionario) => (
        <code className="funcionario-code">{funcionario.codigo}</code>
      ),
    },
    {
      label: "Status",
      name: "ativo",
      size: 2,
      render: (funcionario) => (
        <div className="d-flex align-items-center gap-1">
          <Badge
            bg={funcionario.ativo ? "success" : "secondary"}
            className="d-flex align-items-center gap-1"
          >
            {funcionario.ativo ? "Ativo" : "Inativo"}
          </Badge>
        </div>
      ),
    },
    {
      label: "Data de Criação",
      name: "createdAt",
      size: 2,
      render: (funcionario) => (
        <span className="text-muted">{formatDate(funcionario.createdAt)}</span>
      ),
    },
    {
      label: "",
      size: 2,
      render: (funcionario) => (
        <div>
          <Button
            btnColor="outline-primary"
            onClick={() => handleToggleStatus(funcionario)}
            title={funcionario.ativo ? "Desativar" : "Ativar"}
            style={{ borderRadius: "8px", padding: "8px" }}
          >
            {funcionario.ativo ? <PowerOff size={16} /> : <Power size={16} />}
          </Button>
          <Button
            btnColor="outline-danger"
            onClick={() => handleDeleteFuncionario(funcionario)}
            title="Excluir"
            style={{ borderRadius: "8px", padding: "8px", marginLeft: 8 }}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <MainHeader
        title="Funcionários"
        subtitle="Gerencie os funcionários e seus códigos de acesso"
        icon={<Users size={32} />}
      >
        <Button
          btnColor="outline-primary"
          onClick={() => navigate('/relatorio-produtividade')}
          className="modern-btn d-flex align-items-center gap-2"
          style={{ borderRadius: "12px", padding: "12px 24px" }}
        >
          <BarChart2 size={20} />
          Relatório de Produtividade
        </Button>
        <Button
          btnColor="success"
          onClick={() => {
            setFuncionarioToEdit(null);
            setShowForm(true);
          }}
          className="modern-btn d-flex align-items-center gap-2"
          style={{ borderRadius: "12px", padding: "12px 24px" }}
        >
          <Plus size={20} />
          Novo Funcionário
        </Button>
      </MainHeader>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {funcionarios.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <div className="funcionarios-empty-icon">
                  <Users size={40} />
                </div>
              </div>
              <h5 className="text-muted">Nenhum funcionário cadastrado</h5>
              <p className="text-muted mb-4">
                Comece adicionando um novo funcionário para gerar códigos de
                acesso
              </p>
              <Button
                btnColor="success"
                onClick={() => {
                  setFuncionarioToEdit(null);
                  setShowForm(true);
                }}
              >
                <Plus size={20} className="me-2" />
                Adicionar Primeiro Funcionário
              </Button>
            </div>
          ) : (
            <DataList
              data={funcionarios}
              rowKey={(funcionario) => funcionario.id!}
              disableHoverEffects={true}
              columns={columns}
            />
          )}
        </Card.Body>
      </Card>

      {/* Modal do Formulário */}
      <FuncionarioForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setFuncionarioToEdit(null);
        }}
        onSave={handleAddFuncionario}
        funcionario={funcionarioToEdit}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setFuncionarioToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Excluir Funcionário"
        message={`Tem certeza que deseja excluir o funcionário "${funcionarioToDelete?.nome}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        type="danger"
      />

      {/* Modal de Sucesso com Código */}
      <Modal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          setNewFuncionario(null);
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>✅ Funcionário Criado com Sucesso!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <p className="mb-3">
            O funcionário <strong>{newFuncionario?.nome}</strong> foi criado com
            sucesso!
          </p>
          <div className="alert alert-info mb-3">
            <p className="mb-2">
              <strong>Código de Acesso Gerado:</strong>
            </p>
            <h2
              className="mb-3"
              style={{
                fontFamily: "monospace",
                letterSpacing: "2px",
                fontSize: "2rem",
                color: "#0d6efd",
              }}
            >
              {newFuncionario?.codigo}
            </h2>
            <Button
              btnColor="outline-primary"
              onClick={() => {
                navigator.clipboard.writeText(newFuncionario?.codigo || "");
                toast.success("Código copiado para a área de transferência!");
              }}
            >
              📋 Copiar Código
            </Button>
          </div>
          <p className="text-muted mb-0">
            <small>
              ⚠️ Anote este código! O funcionário precisará dele para fazer
              login.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            btnColor="success"
            onClick={() => {
              setShowSuccessModal(false);
              setNewFuncionario(null);
            }}
          >
            Entendi
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FuncionariosList;
